import { useState, useContext } from 'react';
import SiteContext from '../AppContext';
import cx from 'classnames';
import styles from './mobileSubNavMenu.module.scss';
import { Arrow, Caret } from '../Icons';
import Promoted from './Promoted';
import AccessibleButton from '../AccessibleButton/AccessibleButton';
import GenericLink from '../GenericLink/GenericLink';
import { Minimize, Expand } from '../Icons';

/**
 * @param {object} [props] component props
 * @param {string} [props.label] label of the menu item
 * @param {object} [props.secondaryNavigation] sub nav content
 * @param {object} [props.promoImage] image content
 * @param {string} [props.promoHeader] promoted header
 * @param {string} [props.promoLink] link content
 * @param {string} [props.promoCopy] copy used in the promoted section
 * @param {Function} [props.onBackClick] on click handler for back button
 * @returns {JSX.element} primary nav menu item component
 */
export default function MobileSubNavMenu({
  label,
  secondaryNavigation,
  promoImage,
  promoHeader,
  promoLink,
  promoCopy,
  onBackClick,
}) {
  const [activeColumn, setActiveColumn] = useState(null);
  const { brand } = useContext(SiteContext);

  return (
    <div className={styles.container}>
      <button className={styles.labelContainer} onClick={onBackClick}>
        <Arrow className={styles.arrow} />
        <h3>{label}</h3>
      </button>
      <ul>
        {secondaryNavigation?.map((item, i) => (
          <li key={i}>
            {item?.fields?.navigationItems?.map((link, j) => {
              const isActive = activeColumn === `${i}-${j}`;
              return (
                <AccessibleButton
                  key={j}
                  className={styles.subnavItem}
                  onClick={() =>
                    setActiveColumn((prev) => (prev === `${i}-${j}` ? null : `${i}-${j}`))
                  }>
                  <div className={styles.subnavItemTitle}>
                    <p>{link.fields.title}</p>
                    <div className={styles.subnavItemIcon}>
                      {isActive ? <Minimize /> : <Expand />}
                    </div>
                  </div>
                  <ul
                    className={cx(styles.subnavColumnItems, {
                      [styles.subnavColumnItemsHidden]: !isActive,
                      [styles.subnavColumnItemsActive]: isActive,
                    })}>
                    {link.fields?.links?.map((sublink, k) => (
                      <GenericLink
                        internalLink={sublink?.fields?.internalPage}
                        externalLink={sublink?.fields?.linkAddress}
                        key={k}
                        className={styles.subnavColumnItem}>
                        <span>{sublink.fields?.linkText}</span>
                      </GenericLink>
                    ))}
                  </ul>
                </AccessibleButton>
              );
            })}
            <AccessibleButton
              className={cx(styles.subnavItem, {
                [styles.subnavItemHighlightedLink]: item.fields.isHighlightedLink,
              })}>
              <GenericLink
                internalLink={item?.fields?.link?.fields.internalPage}
                externalLink={item?.fields?.link?.fields.linkAddress}
                key={i}
                className={cx(styles.subnavItemTitle)}>
                <p>{item.fields.link.fields.linkText}</p>
                {brand === 'now-fresh' ? <Arrow /> : <Caret className={styles.rightCaret} />}
              </GenericLink>
            </AccessibleButton>
          </li>
        ))}
      </ul>
      <div>
        {promoImage && (
          <Promoted
            imageUrl={promoImage.fields.file.url}
            imageAlt={promoImage.fields.description}
            promoCopy={promoCopy}
            promoLink={promoLink}
            header={promoHeader}
          />
        )}
      </div>
    </div>
  );
}
